import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { AuthLayout } from "./Login";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

// Approved account owner or invited staff: single-use, expiring link → set password → signed in.
export default function Activate() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { applySession } = useAuth();
  const [info, setInfo] = useState(null);
  const [invalid, setInvalid] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return setInvalid("This link is missing its token.");
    api.get("/auth/activate", { params: { token } }).then((r) => setInfo(r.data)).catch((e) => setInvalid(errorMessage(e)));
  }, [token]);

  const isGmail = /@(gmail|googlemail)\.com$/i.test(info?.email || "");
  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/activate", { token, password });
      applySession(data);
      toast.success("Welcome to Supplivo!");
      navigate(data.user?.role === "owner" ? "/dashboard" : "/scan");
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <AuthLayout title={info?.purpose === "invitation" ? "Accept your invitation" : "Activate your Supplivo account"}>
      {invalid ? <p className="text-sm text-rose-700" data-testid="activate-invalid">{invalid} <Link to="/login" className="underline">Go to login</Link>.</p> : !info ? <p className="text-sm text-slate-500">Checking your link…</p> : (
        <form onSubmit={submit} className="space-y-4" data-testid="activate-form">
          <p className="text-sm text-slate-600" data-testid="activate-info">Hello {info.name || ""} — you're joining <b>{info.organisation_name}</b> as <span className="font-mono">{info.email}</span>.{info.purpose === "invitation" ? "" : " Choose a password to finish."}</p>
          {info.purpose === "invitation" && (
            <div className="space-y-3" data-testid="activate-google-block">
              <p className="text-sm text-slate-600">{isGmail ? "This is a Google account — just continue with Google, no password needed." : "If this email is a Google account (Gmail / Google Workspace) you can continue with Google — no password needed."}</p>
              <Button type="button" variant="outline" onClick={googleSignIn} className="w-full h-11" data-testid="activate-google">
                <svg className="h-4 w-4 mr-2" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.5l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.9 6.1C12.4 13.4 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z"/><path fill="#FBBC05" d="M10.5 28.6A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.1.8-4.6l-7.9-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l7.9-6.1z"/><path fill="#34A853" d="M24 48c6.3 0 11.7-2.1 15.6-5.7l-7.5-5.8c-2.1 1.4-4.8 2.3-8.1 2.3-6.3 0-11.6-3.9-13.5-9.3l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/></svg>
                Continue with Google as {info.email}
              </Button>
              <div className="flex items-center gap-3 text-xs text-slate-400"><span className="h-px flex-1 bg-slate-200" />{isGmail ? "or set a password instead" : "or choose a password"}<span className="h-px flex-1 bg-slate-200" /></div>
            </div>
          )}
          <div className="space-y-1.5"><Label>Password</Label><Input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="activate-password" /></div>
          <div className="space-y-1.5"><Label>Confirm password</Label><Input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="activate-confirm" /></div>
          <Button type="submit" className="w-full h-11 bg-slate-900" disabled={busy} data-testid="activate-submit">{busy ? "Activating…" : "Set password and sign in"}</Button>
        </form>
      )}
    </AuthLayout>
  );
}
